import { useEffect, useRef, useState } from "react";

let nextKey = 0;
const newKey = () => `img-${nextKey++}`;

// An entry is either an image the product already has ({ path }) or a file
// picked in this session ({ file }). `url` is what the tile displays.
export const fromExisting = (paths) =>
  (paths || []).map((path) => ({ key: newKey(), path, url: process.env.REACT_APP_BE_URL + path }));

const fromFiles = (files) =>
  Array.from(files || [])
    .filter((file) => file.type.startsWith("image/"))
    .map((file) => ({ key: newKey(), file, url: URL.createObjectURL(file) }));

// Turns the list into what the API expects: the new files to upload, and the
// final order where "new:<n>" points at the n-th uploaded file.
export const toUploadPayload = (items) => {
  const files = [];
  const order = items.map((item) => {
    if (item.file) {
      files.push(item.file);
      return `new:${files.length - 1}`;
    }
    return item.path;
  });
  return { files, order };
};

const move = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

// Product image picker without a fixed number of slots. The first image is
// the banner (the one shown on listing cards); drag tiles, or use the arrow
// buttons on touch screens, to change the order.
export default function ImageManager({ value, onChange }) {
  const inputRef = useRef();
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [dropActive, setDropActive] = useState(false);

  // Free the preview blobs of files when they leave the list or on unmount
  const urlsRef = useRef(new Set());
  useEffect(() => {
    const current = new Set(value.filter((i) => i.file).map((i) => i.url));
    urlsRef.current.forEach((url) => {
      if (!current.has(url)) URL.revokeObjectURL(url);
    });
    urlsRef.current = current;
  }, [value]);
  useEffect(() => () => urlsRef.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const addFiles = (files) => {
    const added = fromFiles(files);
    if (added.length) onChange([...value, ...added]);
  };

  const remove = (index) => onChange(value.filter((_, i) => i !== index));

  const handleDrop = (index) => {
    if (dragIndex !== null && dragIndex !== index) onChange(move(value, dragIndex, index));
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          if (dragIndex === null && e.dataTransfer.types.includes("Files")) {
            e.preventDefault();
            setDropActive(true);
          }
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={(e) => {
          if (dragIndex !== null) return;
          e.preventDefault();
          setDropActive(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 rounded-xl p-1 transition-colors ${dropActive ? "bg-primary/5 ring-2 ring-primary" : ""}`}
      >
        {value.map((item, index) => {
          const isBanner = index === 0;
          return (
            <div
              key={item.key}
              draggable
              onDragStart={(e) => {
                setDragIndex(index);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => {
                if (dragIndex === null) return;
                e.preventDefault();
                setOverIndex(index);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDrop(index);
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
              className={`group relative aspect-square rounded-xl overflow-hidden bg-surface-container-high cursor-grab active:cursor-grabbing transition-all duration-200
                ${isBanner ? "col-span-2 row-span-2 ring-2 ring-primary" : "border border-outline-variant/30"}
                ${dragIndex === index ? "opacity-40 scale-95" : ""}
                ${overIndex === index && dragIndex !== index ? "ring-2 ring-sky-400 scale-[1.03]" : ""}`}
            >
              <img src={item.url} alt={`Product ${index + 1}`} className="w-full h-full object-cover pointer-events-none select-none" />

              {isBanner ? (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-primary text-on-primary text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow">
                  <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                  Banner
                </span>
              ) : (
                <span className="absolute top-2 left-2 w-6 h-6 bg-black/60 text-white rounded-full text-[11px] font-bold flex items-center justify-center">
                  {index + 1}
                </span>
              )}
              {item.file && (
                <span className="absolute top-2 right-10 bg-green-600 text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded">New</span>
              )}

              <button
                type="button"
                onClick={() => remove(index)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 text-error shadow flex items-center justify-center hover:bg-error hover:text-white transition-colors"
                title="Remove image"
                aria-label={`Remove image ${index + 1}`}
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>

              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 p-1.5 bg-gradient-to-t from-black/60 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => onChange(move(value, index, index - 1))}
                  className="w-7 h-7 rounded-full bg-white/90 text-slate-700 flex items-center justify-center disabled:opacity-30"
                  title="Move left"
                >
                  <span className="material-symbols-outlined text-base">chevron_left</span>
                </button>
                {!isBanner && (
                  <button
                    type="button"
                    onClick={() => onChange(move(value, index, 0))}
                    className="h-7 px-2 rounded-full bg-white/90 text-primary text-[10px] font-bold flex items-center gap-0.5"
                    title="Use as banner image"
                  >
                    <span className="material-symbols-outlined text-sm">star</span>
                    Banner
                  </button>
                )}
                <button
                  type="button"
                  disabled={index === value.length - 1}
                  onClick={() => onChange(move(value, index, index + 1))}
                  className="w-7 h-7 rounded-full bg-white/90 text-slate-700 flex items-center justify-center disabled:opacity-30"
                  title="Move right"
                >
                  <span className="material-symbols-outlined text-base">chevron_right</span>
                </button>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => inputRef.current.click()}
          className={`aspect-square rounded-xl border-2 border-dashed border-outline-variant hover:border-primary hover:bg-primary/5 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-primary transition-colors ${value.length === 0 ? "col-span-2 row-span-2" : ""}`}
        >
          <span className="material-symbols-outlined text-3xl">add_a_photo</span>
          <span className="text-xs font-semibold">{value.length === 0 ? "Add product images" : "Add more"}</span>
          {value.length === 0 && <span className="text-[10px]">First image becomes the banner</span>}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <p className="text-[11px] text-slate-500 leading-snug">
        {value.length} image{value.length === 1 ? "" : "s"} &middot; Select or drop as many as you like. The first image
        is the banner. Drag to reorder, or use the arrows, and press &times; to remove one.
      </p>
    </div>
  );
}
